<template>
  <page-template :is-loading="isLoading" :data-exists="accountDetails !== null">
    <template #pageHeadline>
      {{ $t('accountDetails.pageHeadline') }}
    </template>
    <template #secondLevelHeaderRight>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !accountDetails || Number(accountDetails?.acl) > 20"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('accountDetails.name')"
              :placeholder="$t('accountDetails.name')"
              v-model:inputValue="accountDetails['contact_name']"
              :disabled="Number(accountDetails.acl) > 20"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="email"
              classes="email"
              data-qa="email-input"
              :label="$t('accountDetails.email')"
              :placeholder="$t('accountDetails.email')"
              v-model:inputValue="accountDetails.email"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="phone"
              classes="phone"
              data-qa="phone-input"
              :label="$t('accountDetails.phone')"
              :placeholder="$t('accountDetails.phone')"
              v-model:inputValue="accountDetails.mobile"
              :disabled="Number(accountDetails.acl) > 20"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="organization"
              classes="organization"
              data-qa="organization-input"
              :label="$t('accountDetails.organization')"
              :placeholder="$t('accountDetails.organization')"
              v-model:inputValue="accountDetails['org_name']"
              disabled
            />
          </div>
        </div>
      </div>
      <rbz-card-collapsible data-qa="qr-code-card">
        <template #title>
          {{ $t('accountDetails.qrCodeCard.title') }}
        </template>
        <template #content>
          <div class="qr-code-card-content">
            <canvas ref="canvasQRCode"></canvas>
            <code>{{ otpSeedDisplay }}</code>
            <div class="rbz-text-h3">
              <a
                target="_blank"
                class="apple-link app-link"
                href="https://itunes.apple.com/il/app/google-authenticator/id388497605"
                v-b-tooltip="$t('accountDetails.qrCodeCard.getAppTooltip')"
              >
                <icon-apple color="" />
              </a>
              |
              <a
                target="_blank"
                class="android-link app-link"
                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                v-b-tooltip="$t('accountDetails.qrCodeCard.getAppTooltip')"
              >
                <icon-android color="" />
              </a>
            </div>
          </div>
        </template>
      </rbz-card-collapsible>
      <rbz-card-collapsible
        v-if="Number(accountDetails.acl) > 11"
        class="api-keys-card"
        data-qa="api-keys-card"
      >
        <template #title>
          {{ $t('accountDetails.apiKeys.title') }}
        </template>
        <template #content>
          <rbz-table
            :columns="apiKeysColumns"
            :table-data="apiKeys"
            :row-buttons="apiKeysRowButtons"
            :show-new-as-row="true"
            :show-download-button="false"
            data-qa="api-keys-table"
            @add-inline-data="addAPIKey"
            @delete-row-button-clicked="deleteAPIKey"
          >
            <template #newRowOverride="newRowProps">
              <td colspan="3">
                <rbz-form-input
                  name="title"
                  data-qa="title-input"
                  classes="title"
                  :label="$t('accountDetails.apiKeys.inputLabel')"
                  :placeholder="$t('accountDetails.apiKeys.inputLabel')"
                  v-model:inputValue="newRowProps.data.title"
                />
              </td>
            </template>
          </rbz-table>
          <rbz-loader
            :loading="!!isAPIKeysTableLoading"
            :data-exists="apiKeys !== null"
            height="220px"
          />
        </template>
      </rbz-card-collapsible>
    </template>
  </page-template>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import { UserAPIKey, UserDetails } from '@/types';
import { mapStores } from 'pinia';
import RequestsUtils from '@/helpers/RequestsUtils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzCardCollapsible from 'ui-component-library/rbz/components/RbzCardCollapsible.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import QRCode from 'qrcode';
import DateTimeUtils from 'ui-component-library/helpers/DateTimeUtils';
import { useUserStore } from '@/store/UserStore';
import RbzLoader from '@/components/RbzLoader.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import IconApple from 'ui-component-library/assets/icons/IconApple.vue';
import IconAndroid from 'ui-component-library/assets/icons/IconAndroid.vue';

export default defineComponent({
  name: 'AccountDetails',
  components: {
    IconAndroid,
    IconApple,
    RbzLoader,
    RbzFormButton,
    RbzFormInput,
    PageTemplate,
    RbzCardCollapsible,
    RbzTable,
  },
  data() {
    return {
      accountId: null,
      accountDetails: null as UserDetails,
      pageLoadingCounter: null,
      apiKeysTableLoadingCounter: null,

      apiKeys: null as UserAPIKey[],
      apiKeysColumns: [
        {
          id: 'title',
          title: this.$t('accountDetails.apiKeys.tableColumnTitle'),
          fieldNames: ['title'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'key',
          title: this.$t('accountDetails.apiKeys.tableColumnKey'),
          fieldNames: ['key'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'date',
          title: this.$t('accountDetails.apiKeys.tableColumnDate'),
          fieldNames: ['created_time'],
          displayFunction: (item: UserAPIKey) => {
            const date = new Date(item.created_time * 1000);
            return DateTimeUtils.isoToNowCuriefenseFormat(date);
          },
          tooltipFunction: (item: UserAPIKey) => {
            const date = new Date(item.created_time * 1000);
            const adjustedDate = DateTimeUtils.adjustDateToUTC(date);
            return `${DateTimeUtils.isoToNowFullCuriefenseFormat(adjustedDate)} UTC`;
          },
          classes: 'width-160px',
          cellContentClasses: 'ellipsis',
          isSortable: true,
          isSearchable: true,
        },
      ] as RbzTableColumnOptions[],
      apiKeysRowButtons: [
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
    };
  },
  computed: {
    ...mapStores(useUserStore),

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    isAPIKeysTableLoading(): boolean {
      return this.apiKeysTableLoadingCounter === null || this.apiKeysTableLoadingCounter > 0;
    },

    otpSeedDisplay(): string {
      return this.accountDetails.otpseed?.replace(/(.{4})(.{4})(.{4})(.{4})/, '$1 $2 $3 $4');
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    setAPIKeysTableLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.apiKeysTableLoadingCounter++;
      } else {
        this.apiKeysTableLoadingCounter--;
      }
    },

    async loadAccountDetails() {
      this.setPageLoadingStatus(true);
      const url = `accounts/${this.accountId}`;
      const method = 'GET';
      const response = await RequestsUtils.sendRequest({
        methodName: method,
        url,
      });
      this.accountDetails = response?.data || null;
      this.setPageLoadingStatus(false);
    },

    async loadAPIKeys() {
      if (!this.accountDetails || Number(this.accountDetails.acl) <= 11) {
        return;
      }
      this.setAPIKeysTableLoadingStatus(true);
      const url = `accounts/${this.accountId}/api-keys`;
      const method = 'GET';
      const response = await RequestsUtils.sendRequest({
        methodName: method,
        url,
      });
      this.apiKeys = response?.data?.items || [];
      this.setAPIKeysTableLoadingStatus(false);
    },

    async loadQRCode() {
      const secret = this.accountDetails?.otpseed;
      const otpauth = `otpauth://totp/Reblaze%20Console?secret=${secret}`;
      await QRCode.toCanvas(this.$refs.canvasQRCode, otpauth, {
        width: 300,
      });
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      const url = `accounts/${this.accountId}`;
      const method = 'PUT';
      await RequestsUtils.sendRequest({
        methodName: method,
        data: this.accountDetails,
        url,
        successMessage: `Saved changes successfully!`,
        failureMessage: `Failed saving changes!`,
        displayServerFailureMessage: true,
      }).then(() => {
        this.userStore.accountDataChanged();
        this.loadAccountDetails();
      });
      this.setPageLoadingStatus(false);
    },

    async addAPIKey(newAPIKey: UserAPIKey) {
      this.setAPIKeysTableLoadingStatus(true);
      const url = `accounts/${this.accountId}/api-keys`;
      const methodName = 'POST';
      await RequestsUtils.sendRequest({
        methodName,
        url,
        data: { title: newAPIKey.title },
      });
      await this.loadAPIKeys();
      this.setAPIKeysTableLoadingStatus(false);
    },

    async deleteAPIKey(key: UserAPIKey) {
      this.setAPIKeysTableLoadingStatus(true);
      const url = `accounts/api-keys/${key.id}`;
      const method = 'PUT';
      await RequestsUtils.sendRequest({
        methodName: method,
        url,
      });
      await this.loadAPIKeys();
      this.setAPIKeysTableLoadingStatus(false);
    },
  },
  async mounted() {
    this.setPageLoadingStatus(true);
    this.accountId = this.userStore.getCookieAccountId();
    if (this.accountId) {
      await this.loadAccountDetails();
      await this.loadAPIKeys();
    }
    this.setPageLoadingStatus(false);
    nextTick(() => {
      this.loadQRCode();
    });
  },
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.qr-code-card-content {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.app-link {
  fill: map.get(colors.$colors, 'royal-blue');

  &:hover {
    fill: map.get(colors.$colors, 'mine-shaft');
  }
}
</style>
